/* eslint-disable jsdoc/check-param-names */
import { Dispatch, SetStateAction, useCallback, useRef } from "react";
import { Box, Stack, SxProps, TextField, Typography } from "@mui/material";

const FILTER_ALL  = /[^\x20-\x7E\uFF61-\uFF9F]/g;      // 半角カナ以外を除去

type Props = {
  id: string;
  type: string;
  label: string;
  text: string;
  setText: Dispatch<SetStateAction<any>>;
  maxLength?: number;
  variant?: "outlined" | "filled" | "standard";
  onChange?: Function;
  size?: "small" | "medium";
  fullWidth?: boolean;
  required?: boolean;
  textRequired?: boolean;
  disabled?: boolean;
  isNumericText?: boolean;
  description?: string;
  descriptionSx?: SxProps;
  allowHalfKanaOnly?: boolean;
  error?: string;
};

/**
 * カスタムテキストフィールド
 * @param id フィールドID
 * @param type タイプ
 * @param label ラベル
 * @param text テキスト
 * @param setText テキストをセット
 * @param maxLength 最大文字数
 * @param variant テキストフィールドの種類
 * @param size inputの大きさ
 * @param fullWidth 幅いっぱいにするかどうか
 * @param required 必須かどうか
 * @param textRequired テキストで必須と書くかどうか
 * @param disabled 非活性
 * @param isNumericText 半角数字のみのテキスト
 * @param description 補足説明
 * @param descriptionSx 補足説明のスタイル
 * @param allowHalfKanaOnly 半角、半角ｶﾅのみ許可
 * @param error エラーメッセージ。文字は表示していない。
 * @returns JSX.Element
 */
export default function LabeledTextField({
  id,
  type,
  label,
  text,
  setText,
  maxLength = 100,
  variant = "outlined",
  onChange = () => {},
  size = "small",
  fullWidth,
  required,
  textRequired,
  disabled,
  isNumericText,
  description,
  descriptionSx,
  allowHalfKanaOnly,
  error,
}: Props) {
  // ↓↓↓ 半角ｶﾅのみ許可コード
  const composing = useRef(false);
  const sanitizeHalfKana = (s: string) => (
    // 半角ASCII + 半角カナのみ残す
    s.replace(/[^\x20-\x7E\uFF61-\uFF9F]/g, "")
  );

  const handleChangeHalf = (value: string) => {
    if (composing.current) {
      // 変換中は触らない（IME対策）
      setText(value);
    } else {
      setText(sanitizeHalfKana(value));
    }
    onChange();
  };
  // ↑↑↑ 半角ｶﾅのみ許可コード

  const onChangeText = (value: string) => {
    if (allowHalfKanaOnly) {
      handleChangeHalf(value);
      return;
    }
    if (isNumericText && !/^\d*$/.test(value)) {
      // 数値テキストで数値以外の文字が入力されたら無視
      return;
    }
    setText(value);
    onChange();
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      position="relative"
      height={50}
      width={0}
      margin="0 auto"
    >
      <Box textAlign="right" marginRight={1} position="absolute" top={14} right={10} width="max-content">
        {label}
        {textRequired && (
          <Typography component="span" color="red">
            （必須）
          </Typography>
        )}
        ：
      </Box>
      <Box
        position="absolute"
        width="max-content"
        minWidth={207}
        top={5}
        left={12}
        display="flex"
        flexDirection="row"
      >
        <TextField
          id={id}
          type={type}
          value={text}
          label={label}
          inputProps={{
            maxLength,
            pattern: allowHalfKanaOnly ? "[\\x20-\\x7E\\uFF61-\\uFF9F]*" : {},
          }}
          onCompositionStart={() => {if (allowHalfKanaOnly) composing.current = true}}
          onCompositionEnd={(e) => {
            if (allowHalfKanaOnly) {
              composing.current = false;
              const val = (e.target as HTMLInputElement).value
              const sanitizedVal = sanitizeHalfKana(val);
              if (sanitizedVal !== val) setText(sanitizedVal);
            }
          }}
          variant={variant}
          onChange={(e) => onChangeText(e.target.value)}
          size={size}
          fullWidth={fullWidth}
          required={required}
          disabled={disabled}
          sx={{ maxWidth: 207, width: 207 }}
          error={!!error}
        />
        {description && (
          <Typography marginLeft={2} alignSelf="center" sx={descriptionSx ?? {}}>
            {description}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
